import { getDetail, getSummary } from './shared';

const TRACKED_FIELDS = [
  ['title', 'Title', message => message.title],
  ['summary', 'Summary', message => getSummary(message)],
  ['body', 'Message body', message => message.body?.content],
  ['category', 'Category', message => message.category],
  ['severity', 'Severity', message => message.severity],
  ['services', 'Services', message => message.services],
  ['tags', 'Tags', message => message.tags],
  ['startDateTime', 'Published date', message => message.startDateTime],
  ['endDateTime', 'Expiry date', message => message.endDateTime],
  ['actionRequiredByDateTime', 'Action date', message => message.actionRequiredByDateTime],
  ['roadmap', 'Roadmap ID', message => getDetail(message, 'RoadmapIds')],
  ['platforms', 'Platforms', message => getDetail(message, 'Platforms')],
];

export function normalizeVersionHistory(storedHistory = [], latestMessage) {
  const history = storedHistory.filter(Boolean);
  return history.length > 0
    ? history
    : latestMessage ? [{ ...latestMessage, _version: 1 }] : [];
}

export function stripHtml(value) {
  if (!value) return '';
  const document = new DOMParser().parseFromString(value, 'text/html');
  return (document.body.textContent ?? '').replace(/\s+/g, ' ').trim();
}

export function formatMessageBodyHtml(value = '') {
  if (!value) return '';

  const document = new DOMParser().parseFromString(value, 'text/html');
  document.body.querySelectorAll('p').forEach(paragraph => {
    const text = (paragraph.textContent ?? '').replace(/\s+/g, ' ').trim();
    const headingMatch = text.match(/^\[(.+?)\]$/);
    if (!headingMatch) return;

    const heading = document.createElement('h2');
    heading.textContent = headingMatch[1].replace(/:\s*$/, '').trim();
    paragraph.replaceWith(heading);
  });

  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
  while (walker.nextNode()) {
    walker.currentNode.nodeValue = walker.currentNode.nodeValue.replace(/\[([^\]]+)\]/g, '$1');
  }

  return document.body.innerHTML;
}

export function buildHtmlDiff(currentHtml = '', previousHtml = '') {
  const parser = new DOMParser();
  const currentDocument = parser.parseFromString(currentHtml, 'text/html');
  const previousDocument = parser.parseFromString(previousHtml, 'text/html');
  const textNodes = document => {
    const walker = document.createTreeWalker(document.body, 4);
    const nodes = [];
    while (walker.nextNode()) {
      if (walker.currentNode.nodeValue.trim()) nodes.push(walker.currentNode);
    }
    return nodes;
  };
  const currentTextNodes = textNodes(currentDocument);
  const previousTextNodes = textNodes(previousDocument);

  currentTextNodes.forEach((textNode, index) => {
    const currentText = textNode.nodeValue.trim();
    const previousText = previousTextNodes[index]?.nodeValue.trim() ?? '';
    if (currentText === previousText) return;

    const fragment = currentDocument.createDocumentFragment();
    computeWordDiff(currentText, previousText).forEach(part => {
      if (part.status === 'unchanged') {
        fragment.append(`${part.value} `);
        return;
      }
      const span = currentDocument.createElement('span');
      span.className = part.status === 'added'
        ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
        : 'bg-red-100 text-red-700 line-through dark:bg-red-900/40 dark:text-red-300';
      span.textContent = `${part.value} `;
      fragment.append(span);
    });
    textNode.replaceWith(fragment);
  });

  return currentDocument.body.innerHTML;
}

export function getChangedFields(current, previous) {
  if (!previous) return ['Initial version'];
  return TRACKED_FIELDS
    .filter(([, , read]) => JSON.stringify(read(current) ?? null) !== JSON.stringify(read(previous) ?? null))
    .map(([, label]) => label);
}

export function computeWordDiff(current = '', previous = '') {
  const currentWords = current.split(/\s+/).filter(Boolean);
  const previousWords = previous.split(/\s+/).filter(Boolean);
  const rows = previousWords.length + 1;
  const columns = currentWords.length + 1;
  const lengths = Array.from({ length: rows }, () => Array(columns).fill(0));

  for (let previousIndex = 1; previousIndex < rows; previousIndex++) {
    for (let currentIndex = 1; currentIndex < columns; currentIndex++) {
      lengths[previousIndex][currentIndex] = previousWords[previousIndex - 1] === currentWords[currentIndex - 1]
        ? lengths[previousIndex - 1][currentIndex - 1] + 1
        : Math.max(lengths[previousIndex - 1][currentIndex], lengths[previousIndex][currentIndex - 1]);
    }
  }

  const result = [];
  let previousIndex = previousWords.length;
  let currentIndex = currentWords.length;
  while (previousIndex > 0 || currentIndex > 0) {
    if (previousIndex > 0 && currentIndex > 0 && previousWords[previousIndex - 1] === currentWords[currentIndex - 1]) {
      result.unshift({ value: currentWords[currentIndex - 1], status: 'unchanged' });
      previousIndex--;
      currentIndex--;
    } else if (currentIndex > 0 && (previousIndex === 0 || lengths[previousIndex][currentIndex - 1] >= lengths[previousIndex - 1][currentIndex])) {
      result.unshift({ value: currentWords[currentIndex - 1], status: 'added' });
      currentIndex--;
    } else {
      result.unshift({ value: previousWords[previousIndex - 1], status: 'removed' });
      previousIndex--;
    }
  }

  return result;
}